import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Zap, Tv, Wifi, Receipt, History, Bookmark, CheckCircle2, Building2, Wrench, Home, CreditCard, ArrowRight, Droplets } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
type Category = 'Airtime/Data' | 'Electricity' | 'Cable TV' | 'Internet' | 'Water' | 'Service Charge';
const CATEGORIES = [{
  id: 'Airtime/Data',
  icon: Smartphone,
  label: 'Airtime & Data'
}, {
  id: 'Electricity',
  icon: Zap,
  label: 'Electricity'
}, {
  id: 'Cable TV',
  icon: Tv,
  label: 'Cable TV'
}, {
  id: 'Internet',
  icon: Wifi,
  label: 'Internet'
}, {
  id: 'Water',
  icon: Droplets,
  label: 'Water'
}, {
  id: 'Service Charge',
  icon: Building2,
  label: 'Service Charge'
}] as const;
const PROVIDERS = {
  'Airtime/Data': ['MTN', 'Airtel', 'Glo', '9mobile'],
  Electricity: ['Ikeja Electric', 'Eko Electric', 'Abuja Disco', 'Kano Disco'],
  'Cable TV': ['DSTV', 'GOTV', 'Startimes'],
  Internet: ['Spectranet', 'Smile', 'FibreOne'],
  Water: ['Lagos Water Corp', 'FCT Water Board', 'Ogun State Water Corp', 'Rivers State Water'],
  'Service Charge': ['Oceanbay Estate', 'Lekki Palm City', 'Victoria Crest', 'Royal Pine Estate', 'Banana Island Ikoyi', 'Eko Atlantic', 'Pinnock Beach Estate', 'Chevron Estate Lekki', 'Osapa London Estate', 'Abraham Adesanya Estate', 'Magodo GRA', 'VGC Estate']
};
const PACKAGES = {
  DSTV: [{
    name: 'DSTV Premium',
    price: 29500
  }, {
    name: 'DSTV Compact Plus',
    price: 19800
  }, {
    name: 'DSTV Compact',
    price: 12500
  }, {
    name: 'DSTV Confam',
    price: 7400
  }],
  GOTV: [{
    name: 'GOTV Supa+',
    price: 12500
  }, {
    name: 'GOTV Supa',
    price: 7600
  }, {
    name: 'GOTV Max',
    price: 5300
  }]
};
export const BillPayment: React.FC = () => {
  const {
    state,
    processTransaction
  } = useAppContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'pay' | 'saved' | 'history' | 'facility'>('pay');
  const [category, setCategory] = useState<Category>('Airtime/Data');
  // Form State
  const [provider, setProvider] = useState(PROVIDERS['Airtime/Data'][0]);
  const [accountNumber, setAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [meterType, setMeterType] = useState('Prepaid');
  // UI State
  const [isPinOpen, setIsPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  // Derived
  const billHistory = state.transactions.filter(t => t.type === 'Bills');
  // Reset form when category changes
  const handleCategoryChange = (cat: Category) => {
    setCategory(cat);
    setProvider(PROVIDERS[cat][0]);
    setAccountNumber('');
    setAmount('');
    setSelectedPackage('');
  };
  const handlePackageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const pkgName = e.target.value;
    setSelectedPackage(pkgName);
    // Find price and set amount
    const allPackages = [...(PACKAGES['DSTV'] || []), ...(PACKAGES['GOTV'] || [])];
    const pkg = allPackages.find(p => p.name === pkgName);
    if (pkg) {
      setAmount(pkg.price.toString());
    }
  };
  const handlePaySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountNumber || !amount) {
      toast.error('Please fill all required fields');
      return;
    }
    if (Number(amount) > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    setIsPinOpen(true);
  };
  const confirmPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 4) {
      toast.error('PIN must be 4 digits');
      return;
    }
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsPinOpen(false);
      setPin('');
      const title = category === 'Cable TV' && selectedPackage ? selectedPackage : `${provider} ${category === 'Airtime/Data' ? 'Airtime' : category}`;
      const ref = processTransaction(-Number(amount), 'Bills', title);
      toast.success('Payment Successful!', {
        description: `${formatCurrency(Number(amount))} paid for ${title}. Ref: ${ref}`,
        icon: <CheckCircle2 className="text-brand-primary" data-id="element-400" />
      });
      // Reset form
      setAccountNumber('');
      setAmount('');
      setSelectedPackage('');
    }, 1500);
  };
  return <div className="space-y-6" data-id="element-401">
      {/* Header */}
      <div data-id="element-402">
        <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-403">
          Bill Payments
        </h1>
        <p className="text-brand-text-light text-sm" data-id="element-404">
          Pay utilities, buy airtime, and renew subscriptions instantly.
        </p>
      </div>

      <div className="flex space-x-1 bg-white p-1 rounded-xl border border-gray-100 inline-flex overflow-x-auto max-w-full" data-id="element-405">
        <button onClick={() => setActiveTab('pay')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'pay' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-406">
          <Receipt size={16} data-id="element-407" /> Pay Bills
        </button>
        <button onClick={() => setActiveTab('saved')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'saved' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-408">
          <Bookmark size={16} data-id="element-409" /> Saved Billers
        </button>
        <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'history' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-410">
          <History size={16} data-id="element-411" /> History
        </button>
        <button onClick={() => setActiveTab('facility')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === 'facility' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-412">
          <Building2 size={16} data-id="element-413" /> Facility Mgmt
        </button>
      </div>

      {activeTab === 'pay' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-414">
          {/* Categories Grid */}
          <div className="lg:col-span-1 space-y-3" data-id="element-415">
            <h3 className="font-semibold text-brand-text-dark mb-4" data-id="element-416">
              Select Category
            </h3>
            {CATEGORIES.map(cat => <button key={cat.id} onClick={() => handleCategoryChange(cat.id)} className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${category === cat.id ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark shadow-sm' : 'border-gray-100 bg-white text-gray-600 hover:border-brand-primary/30 hover:bg-gray-50'}`} data-id="element-417">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${category === cat.id ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-418">
                  <cat.icon size={20} data-id="element-419" />
                </div>
                <span className="font-medium" data-id="element-420">{cat.label}</span>
              </button>)}
          </div>

          {/* Dynamic Form */}
          <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8" data-id="element-421">
            <h3 className="font-semibold text-brand-text-dark mb-6 border-b border-gray-100 pb-4 flex items-center gap-2" data-id="element-422">
              {category} Details
            </h3>

            <form onSubmit={handlePaySubmit} className="space-y-6" data-id="element-423">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-424">
                <div data-id="element-425">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-426">
                    Provider
                  </label>
                  <select value={provider} onChange={e => setProvider(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-427">
                    {PROVIDERS[category].map(p => <option key={p} value={p} data-id="element-428">
                        {p}
                      </option>)}
                  </select>
                </div>

                {category === 'Electricity' && <div data-id="element-429">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-430">
                      Meter Type
                    </label>
                    <select value={meterType} onChange={e => setMeterType(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-431">
                      <option data-id="element-432">Prepaid</option>
                      <option data-id="element-433">Postpaid</option>
                    </select>
                  </div>}

                <div className={category === 'Electricity' ? 'md:col-span-2' : ''} data-id="element-434">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-435">
                    {category === 'Airtime/Data' ? 'Phone Number' : category === 'Electricity' ? 'Meter Number' : category === 'Cable TV' ? 'Smartcard Number' : category === 'Water' ? 'Water Account Number' : category === 'Service Charge' ? 'Property/Unit ID' : 'Account Number'}
                  </label>
                  <input type="text" required value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" placeholder={`Enter ${category === 'Airtime/Data' ? 'phone' : category === 'Water' ? 'water account' : category === 'Service Charge' ? 'property/unit' : 'account'} number`} data-id="element-436" />
                </div>

                {category === 'Cable TV' && (provider === 'DSTV' || provider === 'GOTV') && <div className="md:col-span-2" data-id="element-437">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-438">
                        Select Package
                      </label>
                      <select required value={selectedPackage} onChange={handlePackageChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-439">
                        <option value="" disabled data-id="element-440">
                          Choose a package
                        </option>
                        {PACKAGES[provider as keyof typeof PACKAGES]?.map(pkg => <option key={pkg.name} value={pkg.name} data-id="element-441">
                              {pkg.name} - {formatCurrency(pkg.price)}
                            </option>)}
                      </select>
                    </div>}

                <div className="md:col-span-2" data-id="element-442">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-443">
                    Amount (₦)
                  </label>
                  <input type="number" required min="100" value={amount} onChange={e => setAmount(e.target.value)} readOnly={category === 'Cable TV' && !!selectedPackage} className={`w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold ${category === 'Cable TV' && selectedPackage ? 'bg-gray-50 text-gray-600' : ''}`} placeholder="0.00" data-id="element-444" />

                  {category === 'Airtime/Data' && <div className="flex flex-wrap gap-2 mt-3" data-id="element-445">
                      {[100, 200, 500, 1000, 2000, 5000].map(amt => <button key={amt} type="button" onClick={() => setAmount(amt.toString())} className="px-4 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:border-brand-primary hover:text-brand-primary transition-colors bg-white" data-id="element-446">
                          ₦{amt}
                        </button>)}
                    </div>}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex justify-between items-center mt-6" data-id="element-447">
                <div data-id="element-448">
                  <p className="text-sm text-brand-text-light" data-id="element-449">
                    Wallet Balance
                  </p>
                  <p className="font-semibold text-brand-text-dark" data-id="element-450">
                    {formatCurrency(state.user?.walletBalance || 0)}
                  </p>
                </div>
                <button type="submit" disabled={!accountNumber || !amount} className="bg-brand-primary hover:bg-brand-primary-dark text-white px-8 py-3.5 rounded-xl font-semibold transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-451">
                  Pay {amount ? formatCurrency(Number(amount)) : 'Bill'}
                </button>
              </div>
            </form>
          </div>
        </div>}

      {activeTab === 'saved' && <EmptyState icon={Bookmark} title="No saved billers" description="Save your frequent billers for faster payments next time." data-id="element-452" />}

      {activeTab === 'history' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-453">
          {billHistory.length > 0 ? <div className="overflow-x-auto" data-id="element-454">
              <table className="w-full text-left text-sm" data-id="element-455">
                <thead className="bg-gray-50 text-brand-text-light border-b border-gray-100" data-id="element-456">
                  <tr data-id="element-457">
                    <th className="px-6 py-4 font-medium" data-id="element-458">Description</th>
                    <th className="px-6 py-4 font-medium" data-id="element-459">Date</th>
                    <th className="px-6 py-4 font-medium" data-id="element-460">Reference</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-461">Amount</th>
                    <th className="px-6 py-4 font-medium text-center" data-id="element-462">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-463">
                  {billHistory.map(tx => <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-464">
                      <td className="px-6 py-4 font-medium text-brand-text-dark" data-id="element-465">
                        {tx.title}
                      </td>
                      <td className="px-6 py-4 text-brand-text-light" data-id="element-466">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-6 py-4 text-brand-text-light font-mono text-xs" data-id="element-467">
                        {tx.reference}
                      </td>
                      <td className="px-6 py-4 font-bold text-brand-text-dark text-right" data-id="element-468">
                        {formatCurrency(Math.abs(tx.amount))}
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-469">
                        <StatusBadge status={tx.status} data-id="element-470" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="p-8" data-id="element-471">
              <EmptyState icon={History} title="No payment history" description="Your bill payments will appear here." data-id="element-472" />
            </div>}
        </div>}

      {activeTab === 'facility' && <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 text-center max-w-3xl mx-auto mt-8" data-id="element-473">
          <div className="w-20 h-20 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-474">
            <Building2 size={40} className="text-brand-primary" data-id="element-475" />
          </div>
          <h2 className="text-2xl font-bold text-brand-text-dark mb-3" data-id="element-476">
            Facility Management
          </h2>
          <p className="text-brand-text-light mb-8 max-w-lg mx-auto" data-id="element-477">
            Manage your properties, pay rent seamlessly, and submit maintenance
            requests all in one dedicated hub.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 text-left" data-id="element-478">
            <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100" data-id="element-479">
              <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-4" data-id="element-480">
                <CreditCard size={20} data-id="element-481" />
              </div>
              <h4 className="font-semibold text-brand-text-dark mb-1" data-id="element-482">
                Rent Payments
              </h4>
              <p className="text-sm text-brand-text-light" data-id="element-483">
                Pay rent securely and track payment history.
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100" data-id="element-484">
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4" data-id="element-485">
                <Wrench size={20} data-id="element-486" />
              </div>
              <h4 className="font-semibold text-brand-text-dark mb-1" data-id="element-487">
                Maintenance
              </h4>
              <p className="text-sm text-brand-text-light" data-id="element-488">
                Submit and track repair requests easily.
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100" data-id="element-489">
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center mb-4" data-id="element-490">
                <Home size={20} data-id="element-491" />
              </div>
              <h4 className="font-semibold text-brand-text-dark mb-1" data-id="element-492">
                Property Overview
              </h4>
              <p className="text-sm text-brand-text-light" data-id="element-493">
                View lease details and property information.
              </p>
            </div>
          </div>

          <button onClick={() => navigate('/dashboard/facility')} className="bg-brand-primary hover:bg-brand-primary-dark text-white px-8 py-3.5 rounded-xl font-semibold transition-colors shadow-md inline-flex items-center gap-2" data-id="element-494">
            Go to Facility Management <ArrowRight size={18} data-id="element-495" />
          </button>
        </div>}

      {/* PIN Modal */}
      <Modal isOpen={isPinOpen} onClose={() => setIsPinOpen(false)} title="Confirm Payment" data-id="element-496">
        <form onSubmit={confirmPayment} className="space-y-6" data-id="element-497">
          <div className="text-center" data-id="element-498">
            <p className="text-brand-text-light mb-2" data-id="element-499">You are about to pay</p>
            <p className="text-3xl font-bold text-brand-text-dark" data-id="element-500">
              {formatCurrency(Number(amount))}
            </p>
            <p className="text-sm font-medium text-brand-text-dark mt-2" data-id="element-501">
              for {provider} {category}
            </p>
          </div>

          <div data-id="element-502">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-center" data-id="element-503">
              Enter 4-Digit PIN
            </label>
            <div className="flex justify-center" data-id="element-504">
              <input type="password" maxLength={4} required value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} className="w-32 text-center tracking-[1em] px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-2xl font-bold" placeholder="••••" data-id="element-505" />
            </div>
          </div>

          <button type="submit" disabled={isProcessing || pin.length !== 4} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-70 flex items-center justify-center" data-id="element-506">
            {isProcessing ? 'Processing...' : 'Confirm Payment'}
          </button>
        </form>
      </Modal>
    </div>;
};